import { createAssetsQuery } from "@/abilities/product-and-asset/graphql/assets.query";
import { generateDynamicSearchAssetsOnlyAggregationsQuery } from "@/abilities/product-and-asset/helpers/product-asset-search-helper";
import { createDataSource, createListCards } from "@/abilities/product-and-asset/pages/assets-overview.model";
import { convertAggregationsResult } from "@/shared/aggregations/aggregations-resolver";
import { SortEntry } from "@/shared/components/sort-dropdown/sort-dropdown.model";
import { useDataDisplayConfig } from "@/shared/data-display-config/composable/data-display-config";
import { DataDisplayConfigId } from "@/shared/data-display-config/composable/data-display.model";
import { INIT_LOAD_AMOUNT } from "@/shared/environment/consts.ts";
import { getSortEntries } from "@/shared/environment/filter/filter-types";
import { generateOrGroupsByFacetsSelected } from "@/shared/facets/facets-filter-generator";
import { Facet, FacetSelected } from "@/shared/facets/facets.model";
import { localizeSearchFields } from "@/shared/search/localize-search-fields";
import { DataSearchConfigId, useSearchConfig } from "@/shared/search/search-config";
import { generateSearchFieldsFilterClauseRegex } from "@/shared/search/search-fields-filter-clause";
import { SearchContext } from "@/shared/search/search.types";
import { consumerClient } from "@/shared/services/apollo-clients/consumer-client";
import { AssetQueryResult } from "@/shared/services/graphql/generated/admin-graph-types";
import { AssetFilter } from "@/shared/services/graphql/generated/consumer-graph-types";
import { useLanguageStore } from "@/shared/store/language-store";
import { useQuery } from "@vue/apollo-composable";
import { computed, ref, Ref } from "vue";
import { useI18n } from "vue-i18n";

export const useAssetDataSource = (
    searchContext: Ref<SearchContext>,
    showJumpToProduct: boolean,
    emitSelectionInTiles: boolean,
    selected: Ref<FacetSelected[]>,
    sort: Ref<SortEntry | undefined>,
    facet: Ref<Facet | undefined>,
    assetIds: Ref<string[] | undefined>
) => {
    const isLoading = ref<boolean>(false);

    const { t } = useI18n();
    const languageStore = useLanguageStore();
    const { result: assetSearchConfigResult } = useSearchConfig(DataSearchConfigId.asset);

    function generateAssetFilterVariables(
        searchContext: SearchContext,
        selected: FacetSelected[],
        sort: SortEntry | undefined
    ) {
        const searchPhrase = searchContext.text ?? "";
        const filteredSearchFields = localizeSearchFields(
            assetSearchConfigResult.value?.searchEntries ?? [],
            languageStore.dataQueryLanguages
        );
        const searchFilterClause = generateSearchFieldsFilterClauseRegex(searchPhrase, filteredSearchFields, true);

        const assetFilter: AssetFilter = {};
        //The wildcard search with * behaves like empty-string search, otherwise the regex will not fit and no results will be returned (see QIT-2531).
        if (searchContext.text && searchContext.text !== "*") {
            assetFilter.orGroup = [
                {
                    regex: {
                        assetId: `/${searchContext.text}.*/i`,
                    },
                    boost: 10,
                },
                ...(searchFilterClause.orGroup ?? []),
            ];
        }

        const copySelected = [...selected];
        const orGroups = generateOrGroupsByFacetsSelected(copySelected);

        if (orGroups.length > 0) {
            assetFilter.andGroup = orGroups;
        }

        if (assetIds.value && assetIds.value.length > 0) {
            assetFilter.ids = assetIds.value;
        }

        assetFilter.equals = searchContext.product ? { productId: searchContext.product } : undefined;

        const sortEntries = getSortEntries(sort, languageStore.dataQueryLanguages);
        return {
            filter: assetFilter,
            acceptedLanguages: languageStore.dataQueryLanguages,
            sort: sortEntries,
            first: INIT_LOAD_AMOUNT,
        };
    }

    const query = computed(() => {
        if (dataDisplayResult.value)
            return createAssetsQuery(
                dataDisplayResult.value.datafieldsFragment.datafields,
                dataDisplayResult.value.datafieldsFragment.localizationDatafields
            );

        return createAssetsQuery({}, {});
    });

    const filter = computed(() => {
        //remove own facet from selected
        const selectedCopy = [...selected.value.filter((x) => x.referencedId !== facet.value?.referencedId)];

        return generateAssetFilterVariables(searchContext.value, selectedCopy, sort.value);
    });

    const {
        result: dataDisplayResult,
        loading: dataConfigLoading,
        error: dataError,
    } = useDataDisplayConfig(DataDisplayConfigId.assetSelection, t);

    const {
        error: assetsError,
        result,
        fetchMore,
        refetch: refetchQuery,
    } = useQuery(
        query,
        filter,
        computed(() => ({
            enabled: !!(dataDisplayResult.value && sort.value),
        }))
    );

    const loading = computed(() => {
        return isLoading.value || dataConfigLoading.value;
    });

    const error = computed(() => {
        return dataError.value || assetsError.value;
    });

    const assets = computed(() => {
        return result.value?.assets;
    });

    const dataSource = createDataSource(showJumpToProduct, emitSelectionInTiles);

    const listCards = computed(() => {
        return createListCards(dataSource, assets.value ? (assets.value as AssetQueryResult).assets : []);
    });

    const refetch = async () => {
        isLoading.value = true;

        const assetFilter = generateAssetFilterVariables(searchContext.value, selected.value, sort.value);
        await refetchQuery(assetFilter);

        isLoading.value = false;
    };

    const fetchAggregation = async (searchPhrase: string) => {
        if (!facet.value) return {};

        const aggregationResult = await consumerClient.query({
            query: generateDynamicSearchAssetsOnlyAggregationsQuery(facet.value, searchPhrase),
            variables: filter.value,
        });

        const convertedAggregations = convertAggregationsResult(
            facet.value,
            aggregationResult.data.assets.aggregations
        );

        return convertedAggregations[facet.value.id];
    };

    return {
        refetch,
        loading,
        error,
        listCards,
        assets,
        fetchMore,
        fetchAggregation,
    };
};
